import React, { FC, useEffect } from "react";

// i18n
import messages from "./messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// UI
import { Box } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";

// hooks
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";

// types
import { IOutlets } from "@Libs/Transclusion/types/outlets";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { useStripe } from "@stripe/react-stripe-js";
import { usePayFlow } from "@modules/PaymentProcessing/hooks/usePayFlow";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { withStripe } from "@modules/PaymentProcessing/providers/stripe/hocs/withStripe";
import { useLocation } from "@Libs/Router/hooks/useLocation";
import { ResponsiveFixedButton } from "@UI/Components/ResponsiveFixedButton/ResponsiveFixedButton";
import { usePaymentSetup } from "@modules/PaymentProcessing/hooks/usePaymentSetup";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { Money } from "@Shared/Components/Money/Money";
import { stringify as queryStringStringify } from "qs";

type Props = Omit<
  IOutlets["configure-payment.body"]["props"],
  "paymentMethod" | "basket"
> & {
  onError?: () => void;
};

export const PayWithBancontact: FC<Props> = withStripe(
  ({ onSubmit, onError, onSetupError }) => {
    const stripe = useStripe();
    const { pathname } = useLocation();
    const { currentUser } = useCurrentUser();
    const redirectStatus = useSearchParams("redirect_status");
    const { queryParams } = useLocation();
    const {
      state: { resourceId, resourceType, relatedServiceId },
    } = usePayFlow();
    const isSuccessfullAfterRedirect = redirectStatus === "succeeded";

    const { setupConfig, isLoading, invalidate, isError } = usePaymentSetup({
      paymentMethod: PaymentMethodType.BANCONTACT,
      paymentProvider: "stripe",
      resourceId,
      resourceType,
      onError: onSetupError,
      enabled: !isSuccessfullAfterRedirect,
      relatedServiceId,
    });
    const query = queryStringStringify({
      paymentMethod: PaymentMethodType.BANCONTACT,
      resourceId,
      resourceType,
      returnUrl: queryParams.returnUrl,
      context: queryParams.context,
    });

    const returnUrl = `${window.location.origin}${pathname}?${query}&isPaymentProcessing=true`;

    useEffect(() => {
      if (isSuccessfullAfterRedirect) {
        onSubmit({
          provider: "stripe",
          paymentMethod: PaymentMethodType.BANCONTACT,
          isPaymentConfirmed: true,
        });
      } else if (redirectStatus === "failed") {
        onError?.();
      }
    }, [redirectStatus]);

    useEffect(() => {
      return () => {
        invalidate();
      };
    }, []);

    const payWithBancontact = useDataMutation(async () => {
      try {
        const { error } = await stripe.confirmBancontactPayment(
          setupConfig.clientSecret as string,
          {
            payment_method: {
              billing_details: {
                name: [currentUser?.firstName, currentUser?.lastName].join(" "),
              },
            },
            return_url: returnUrl,
          }
        );

        if (error) {
          throw new Error(String(error));
        }
      } catch (error) {
        console.error(error);
      }
    });

    if (isLoading || isError || isSuccessfullAfterRedirect) {
      return <LoadingScreen />;
    }

    return (
      <Box>
        <Text mb="6" textAlign="center">
          <FormattedMessage {...messages.youWillBeRedirectedToYourBank} />
        </Text>

        <ResponsiveFixedButton
          data-testid="submit-bancontact-button"
          isLoading={payWithBancontact.isLoading}
          h={12}
          width="full"
          colorScheme="seaGreen"
          onClick={() => payWithBancontact.mutate()}
          isDisabled={payWithBancontact.isLoading}
        >
          <FormattedMessage
            {...messages.pay}
            values={{
              amount: (
                <Money
                  cents={setupConfig.amountCents}
                  currency={setupConfig.currency}
                />
              ),
            }}
          />
        </ResponsiveFixedButton>
      </Box>
    );
  }
);
